// ═══════════════════════════════════════════════════════════════════════════════
// DOMAIN BRIDGE — Correspondances entre domaines (civil / militaire / autres)
// PESACORE universaliste : mêmes postures, contextes différents.
// ═══════════════════════════════════════════════════════════════════════════════

// ── Projection d'un scénario dans un domaine ─────────────────────────────────

export const getScenarioProjection = (scenario, domainId) => {
  if (!scenario || !domainId) return null;
  return scenario.projections?.[domainId] || null;
};

// ── Récupération de tous les scénarios applicables à un domaine ──────────────

export const getScenariosForDomain = (config, domainId) => {
  if (!config?.scenarios || !domainId) return [];
  return config.scenarios.filter(s => s.projections?.[domainId]);
};

// ── Récupération des postures partagées entre plusieurs domaines ─────────────

export const getPosturesForDomain = (config, domainId) => {
  if (!config?.archetypes || !domainId) return [];
  return config.archetypes.filter(a => a.domains?.includes(domainId));
};

// ── Recherche des postures communes entre deux domaines ──────────────────────

export const findSharedPostures = (config, domainA, domainB) => {
  if (!config?.archetypes) return [];

  const posturesA = new Set(getPosturesForDomain(config, domainA).map(a => a.id));
  const posturesB = new Set(getPosturesForDomain(config, domainB).map(a => a.id));

  return config.archetypes.filter(a =>
    posturesA.has(a.id) && posturesB.has(a.id)
  );
};

// ── Recherche des ponts entre deux domaines ──────────────────────────────────

export const findBridge = (config, domainA, domainB) => {
  if (!config?.domainMapping?.bridges) return null;

  return config.domainMapping.bridges.find(b =>
    (b.from === domainA && b.to === domainB) ||
    (b.from === domainB && b.to === domainA)
  );
};

// ── Domaines accessibles depuis un domaine donné ─────────────────────────────

export const getReachableDomains = (config, fromDomain) => {
  if (!config?.domainMapping?.bridges) return [];

  const reachable = new Set();
  config.domainMapping.bridges.forEach(b => {
    if (b.from === fromDomain) reachable.add(b.to);
    if (b.to === fromDomain) reachable.add(b.from);
  });

  return Array.from(reachable);
};

// ── Scénarios partagés entre deux domaines ───────────────────────────────────

export const findSharedScenarios = (config, domainA, domainB) => {
  if (!config?.scenarios) return [];

  return config.scenarios.filter(s =>
    s.projections?.[domainA] && s.projections?.[domainB]
  );
};

// ── Vue "carte des ponts" : graphe des domaines connectés ────────────────────

export const getDomainGraph = (config) => {
  if (!config?.domains || !config?.domainMapping?.bridges) {
    return { nodes: [], edges: [] };
  }

  const nodes = config.domains.map(d => ({
    id: d.id,
    label: d.label,
    color: d.color,
  }));

  const edges = config.domainMapping.bridges.map(b => ({
    id: b.id,
    from: b.from,
    to: b.to,
    label: b.label,
    description: b.description,
    sharedScenarios: b.sharedScenarios || [],
  }));

  return { nodes, edges };
};

// ── Analyse d'une posture : dans quels domaines est-elle pertinente ? ────────

export const analyzePosture = (config, archetypeId) => {
  if (!config?.archetypes) return null;

  const archetype = config.archetypes.find(a => a.id === archetypeId);
  if (!archetype) return null;

  const domains = archetype.domains || [];

  // Scénarios où cette posture apparaît (durable ou critique)
  const relevantScenarios = (config.scenarios || []).filter(s =>
    s.durablePostures?.includes(archetypeId) ||
    s.criticalPostures?.includes(archetypeId)
  );

  // Ponts inter-domaines impliqués
  const bridges = (config.domainMapping?.bridges || []).filter(b =>
    domains.includes(b.from) || domains.includes(b.to)
  );

  return {
    archetype,
    domains,
    scenarios: relevantScenarios.map(s => ({
      id: s.id,
      label: s.label,
      role: s.durablePostures?.includes(archetypeId) ? "durable" : "critique",
    })),
    bridges,
  };
};

// ── Matrice de compatibilité entre domaines ──────────────────────────────────

export const getDomainCompatibilityMatrix = (config) => {
  if (!config?.domains) return {};

  const domains = config.domains.map(d => d.id);
  const matrix = {};

  domains.forEach(a => {
    matrix[a] = {};
    domains.forEach(b => {
      if (a === b) {
        matrix[a][b] = 1.0; // identité
        return;
      }
      const shared = findSharedScenarios(config, a, b).length;
      const total = config.scenarios?.length || 1;
      matrix[a][b] = Math.round((shared / total) * 100) / 100;
    });
  });

  return matrix;
};